<template>
  <div class="messages-page">
    <div class="row h-100 g-0">
      <!-- Threads List -->
      <div class="col-md-4 col-lg-3 border-end bg-white overflow-auto h-100">
        <div class="p-3 border-bottom sticky-top bg-white z-index-1">
          <div class="col">
            <router-link to="/work/tickets" class="btn btn-secondary btn-no-r">
              <span><BaseIcon name="active" /> {{ $t("work.tickets") }}</span>
            </router-link>
            <router-link to="/work/messages" class="btn btn-no-l btn-primary">
              <span
                ><BaseIcon name="messages" /> {{ $t("work.messages") }}</span
              >
            </router-link>
          </div>
        </div>
        <div class="scrollable-list">
          <div class="list-group list-group-flush">
            <button
              v-for="thread in filteredThreads"
              :key="thread.id"
              @click="selectThread(thread)"
              class="list-group-item list-group-item-action p-3 border-bottom-0"
              :class="{ 'active-thread': selectedThread?.id === thread.id }"
            >
              <div
                class="d-flex justify-content-between align-items-center mb-1"
              >
                <div class="d-flex align-items-center gap-2">
                  <span
                    class="
                      badge
                      bg-white
                      text-primary
                      border border-primary
                      small
                      rounded-pill
                    "
                    v-if="thread.tickets?.length"
                  >
                    #{{ thread.tickets[0].id }}
                  </span>
                  <span
                    v-if="thread.unread_messages_count > 0"
                    class="badge bg-primary rounded-pill p-1"
                    style="width: 8px; height: 8px"
                    title="Unread messages"
                  ></span>
                </div>
                <small class="text-muted">{{
                  formatDate(
                    lastMessage(thread)?.posted_at || thread.updated_at
                  )
                }}</small>
              </div>
              <h6
                class="mb-1 text-truncate"
                :class="{ 'fw-bold': thread.unread_messages_count > 0 }"
              >
                {{ thread.subject || thread.email || "No Subject" }}
              </h6>
              <p class="mb-0 text-muted small text-truncate">
                {{ lastMessage(thread)?.text }}
              </p>
              <div class="mt-2 d-flex flex-wrap gap-2">
                <BaseIcon :name="getIconForThread(thread)" class="small-icon" />
                <span
                  v-if="thread.order_id"
                  class="badge bg-light text-dark border small"
                >
                  <i class="bi bi-cart3"></i> #{{
                    thread.order?.pretty_id || thread.order_id
                  }}
                </span>
                <span
                  v-if="thread.tickets?.[0]"
                  class="badge rounded-pill"
                  :class="getTicketPriorityClass(thread.tickets[0].priority)"
                >
                  {{ getTicketPriorityName(thread.tickets[0].priority) }}
                </span>
                <span
                  v-if="thread.tickets?.[0]"
                  class="badge rounded-pill"
                  :class="getTicketStatusClass(thread.tickets[0].status)"
                >
                  {{ getTicketStatusName(thread.tickets[0].status) }}
                </span>
              </div>
            </button>
          </div>
          <div class="p-3 text-center" v-if="hasNextPage">
            <button
              class="btn btn-sm btn-outline-primary"
              @click="loadMore"
              :disabled="loading"
            >
              {{ loading ? "Loading..." : "Load more" }}
            </button>
          </div>
        </div>
      </div>

      <!-- Chat Area -->
      <div
        class="
          col-md-8 col-lg-6
          bg-white
          d-flex
          flex-column
          h-100
          overflow-hidden
        "
        v-if="selectedThread"
      >
        <!-- Chat Header -->
        <div
          class="
            p-3
            bg-white
            border-bottom
            d-flex
            justify-content-between
            align-items-center
          "
        >
          <div>
            <h5 class="mb-0">{{ selectedThread.subject || "Conversation" }}</h5>
            <small class="text-muted">{{ selectedThread.email }}</small>
          </div>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-info btn-sm"
              @click="reviewThreadByAi"
              :disabled="reviewingAi"
              v-if="!selectedThread.tickets?.length"
            >
              <i class="bi bi-robot"></i>
              {{ reviewingAi ? "AI Analyzing..." : "Review by AI" }}
            </button>
            <button
              class="btn btn-outline-primary btn-sm"
              @click="showTicketModal = true"
              v-if="!selectedThread.tickets?.length"
            >
              <i class="bi bi-plus-circle"></i> Create Ticket
            </button>
            <router-link
              :to="'/customers/profile/' + selectedThread.email"
              class="btn btn-outline-secondary btn-sm"
              v-if="selectedThread.email"
            >
              <i class="bi bi-person"></i> Profile
            </router-link>
          </div>
        </div>

        <!-- Messages History -->
        <div
          class="
            scrollable-list
            flex-grow-1
            p-4
            overflow-auto
            d-flex
            flex-column
            gap-3
            bg-white
          "
          ref="messageContainer"
          style="
            background-image: radial-gradient(#e9ecef 1px, transparent 1px);
            background-size: 20px 20px;
          "
        >
          <div
            v-for="msg in selectedThread.messages"
            :key="msg.id"
            :class="[
              'message-bubble',
              msg.is_incoming === 0
                ? 'sent align-self-end'
                : 'received align-self-start',
            ]"
          >
            <div class="message-content p-3 rounded-3 shadow-sm">
              <div class="d-flex justify-content-between mb-1 gap-4">
                <small class="fw-bold">{{
                  msg.is_incoming === 0
                    ? "You"
                    : selectedThread.email || "Customer"
                }}</small>
                <small class="text-muted opacity-75">{{
                  formatDate(msg.posted_at)
                }}</small>
              </div>
              <div class="message-text">{{ msg.text }}</div>
            </div>
          </div>
        </div>

        <!-- Message Input -->
        <div class="p-3 bg-white border-top">
          <div class="input-group">
            <textarea
              class="form-control"
              rows="2"
              :placeholder="$t('messages.typeMessage')"
              v-model="newMessageText"
            ></textarea>
            <button class="btn btn-primary px-4" @click="sendMessage">
              <i class="bi bi-send"></i>
            </button>
          </div>
          <div class="mt-2 d-flex gap-2">
            <!-- Internal Note toggle removed from here -->
          </div>
        </div>
      </div>

      <!-- Detail Side Panel -->
      <div
        class="col-md-12 col-lg-3 border-start bg-white overflow-auto h-100"
        v-if="selectedThread"
      >
        <!-- Order Context -->
        <div class="p-4 border-bottom" v-if="selectedThread.order">
          <h6 class="mb-3 text-uppercase small fw-bold text-muted">
            Order Details
          </h6>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="fw-bold">#{{ selectedThread.order.pretty_id }}</span>
            <span
              class="badge"
              :class="statuses[selectedThread.order.status]?.color"
            >
              {{ statuses[selectedThread.order.status]?.name }}
            </span>
          </div>
          <p class="small text-muted mb-3">
            Placed on {{ formatDate(selectedThread.order.created_at) }}
          </p>

          <div class="d-grid gap-2 mb-3">
            <router-link
              :to="'/sales/' + selectedThread.order.id"
              class="btn btn-light btn-sm border"
            >
              <i class="bi bi-box-arrow-up-right"></i> View Order Page
            </router-link>
          </div>

          <div class="small">
            <div class="d-flex justify-content-between mb-1">
              <span class="text-muted">Payment:</span>
              <span
                class="badge"
                :class="
                  paymentStatuses[selectedThread.order.payment_status]?.color
                "
              >
                {{ paymentStatuses[selectedThread.order.payment_status]?.name }}
              </span>
            </div>
            <div class="d-flex justify-content-between">
              <span class="text-muted">Delivery:</span>
              <span
                class="badge"
                :class="
                  deliveredStatuses[selectedThread.order.delivered_status]
                    ?.color
                "
              >
                {{
                  deliveredStatuses[selectedThread.order.delivered_status]?.name
                }}
              </span>
            </div>
          </div>
          <!-- Deliveries List -->
          <div
            v-if="
              selectedThread.order.orderDeliveries &&
              selectedThread.order.orderDeliveries.length
            "
            class="mb-3"
          >
            <h6 class="text-uppercase x-small fw-bold text-muted mb-2">
              Deliveries
            </h6>
            <div
              v-for="delivery in selectedThread.order.orderDeliveries"
              :key="delivery.id"
              class="p-2 border rounded bg-light mb-2 small"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span>
                  <i class="bi bi-truck"></i>
                  {{ delivery.public_tracking_code || "No code" }}
                </span>
                <button
                  v-if="delivery.public_tracking_code"
                  class="btn btn-link btn-sm p-0 text-decoration-none"
                  @click="trackShipping(delivery)"
                >
                  Track
                </button>
              </div>
              <div class="text-muted x-small" v-if="delivery.posted_at">
                Posted: {{ formatDate(delivery.posted_at) }}
              </div>
            </div>
          </div>
          <template v-else>
            <h6 class="text-uppercase x-small fw-bold text-muted mb-2">
              Deliveries
            </h6>
            <div class="alert alert-light border small py-2 mb-3">
              <i class="bi bi-info-circle"></i> No deliveries created yet.
            </div>
          </template>
        </div>

        <!-- Ticket Context -->
        <div class="p-4 border-bottom" v-if="selectedThread?.tickets?.length">
          <template v-for="ticket in selectedThread.tickets" :key="ticket?.id">
            <div class="mb-4">
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <h6 class="text-uppercase small fw-bold text-muted mb-0">
                  Ticket #{{ ticket?.id }}
                </h6>
                <span
                  class="badge"
                  :class="getTicketStatusClass(ticket.status)"
                  >{{ ticket.status }}</span
                >
              </div>

              <div class="mb-3">
                <div class="fw-bold mb-1">{{ ticket.title || "No Title" }}</div>
                <div class="small text-muted" v-if="ticket.description">
                  {{ ticket.description }}
                </div>
              </div>

              <div class="row g-2 mb-2">
                <div class="col-6">
                  <label
                    class="
                      x-small
                      text-muted text-uppercase
                      fw-bold
                      d-block
                      mb-1
                    "
                    >Category</label
                  >
                  <div
                    v-if="
                      editingTicketId !== ticket?.id ||
                      editingTicketField !== 'category'
                    "
                    @click="setEditing(ticket?.id, 'category')"
                    class="
                      clickable-field
                      p-1
                      rounded
                      small
                      border-bottom border-transparent
                    "
                  >
                    {{ getTicketCategoryName(ticket?.category) }}
                  </div>
                  <v-select
                    v-else
                    class="v-select-sm"
                    v-model="ticket.category"
                    :options="filteredTICKET_CATEGORIES"
                    :reduce="(cat) => cat.id"
                    label="name"
                    :clearable="false"
                    @update:modelValue="updateTicketStatus(ticket)"
                    autofocus
                  />
                </div>
                <div class="col-6">
                  <label
                    class="
                      x-small
                      text-muted text-uppercase
                      fw-bold
                      d-block
                      mb-1
                    "
                    >Priority</label
                  >
                  <div
                    v-if="
                      editingTicketId !== ticket?.id ||
                      editingTicketField !== 'priority'
                    "
                    @click="setEditing(ticket?.id, 'priority')"
                    class="
                      clickable-field
                      p-1
                      rounded
                      small
                      border-bottom border-transparent
                    "
                  >
                    <span
                      class="badge"
                      :class="getTicketPriorityClass(ticket.priority)"
                    >
                      {{ getTicketPriorityName(ticket.priority) }}
                    </span>
                  </div>
                  <v-select
                    v-else
                    class="v-select-sm"
                    v-model="ticket.priority"
                    :options="filteredTICKET_PRIORITIES"
                    :reduce="(prio) => prio.id"
                    label="name"
                    :clearable="false"
                    @update:modelValue="updateTicketStatus(ticket)"
                    autofocus
                  />
                </div>
              </div>

              <div class="mb-2">
                <label
                  class="x-small text-muted text-uppercase fw-bold d-block mb-1"
                  >Category</label
                >
                <div
                  v-if="
                    editingTicketId !== ticket?.id ||
                    editingTicketField !== 'contact_category_id'
                  "
                  @click="setEditing(ticket?.id, 'contact_category_id')"
                  class="
                    clickable-field
                    p-1
                    rounded
                    small
                    border-bottom border-transparent
                  "
                >
                  {{ getContactCategoryName(ticket.contact_category_id) }}
                </div>
                <v-select
                  v-else
                  class="v-select-sm"
                  v-model="ticket.contact_category_id"
                  :options="contactCategories"
                  :reduce="(c) => c.id"
                  label="display_name"
                  @update:modelValue="updateTicketStatus(ticket)"
                  autofocus
                />
              </div>
              <div class="mb-2">
                <label
                  class="x-small text-muted text-uppercase fw-bold d-block mb-1"
                  >Assigned To</label
                >
                <div
                  v-if="
                    editingTicketId !== ticket?.id ||
                    editingTicketField !== 'assigned_to_user_id'
                  "
                  @click="setEditing(ticket?.id, 'assigned_to_user_id')"
                  class="
                    clickable-field
                    p-1
                    rounded
                    small
                    border-bottom border-transparent
                  "
                >
                  {{ getUserName(ticket.assigned_to_user_id) }}
                </div>
                <v-select
                  v-else
                  class="v-select-sm"
                  v-model="ticket.assigned_to_user_id"
                  :options="users"
                  :reduce="(user) => user.id"
                  label="full_name"
                  placeholder="Unassigned"
                  @update:modelValue="updateTicketUser(ticket)"
                  autofocus
                />
              </div>

              <div>
                <label
                  class="x-small text-muted text-uppercase fw-bold d-block mb-1"
                  >Status</label
                >
                <div
                  v-if="
                    editingTicketId !== ticket?.id ||
                    editingTicketField !== 'status'
                  "
                  @click="setEditing(ticket?.id, 'status')"
                  class="
                    clickable-field
                    p-1
                    rounded
                    small
                    border-bottom border-transparent
                  "
                >
                  <span
                    class="badge"
                    :class="getTicketStatusClass(ticket?.status)"
                    >{{ getTicketStatusName(ticket?.status) }}</span
                  >
                </div>
                <select
                  v-else
                  class="form-select form-select-sm"
                  v-model="ticket.status"
                  @change="updateTicketStatus(ticket)"
                  @blur="editingTicketId = null"
                  autofocus
                >
                  <option
                    v-for="s in TICKET_STATUSES"
                    :key="s.id"
                    :value="s.id"
                  >
                    {{ s.name }}
                  </option>
                </select>
              </div>
            </div>
            <!-- Related Tasks -->
            <div class="mt-3">
              <label
                class="x-small text-muted text-uppercase fw-bold d-block mb-1"
                >Related Tasks</label
              >
              <div
                v-if="ticket?.work_tasks && ticket?.work_tasks?.length"
                class="small"
              >
                <div
                  v-for="task in ticket.work_tasks"
                  :key="task?.id || $index"
                  class="
                    d-flex
                    justify-content-between
                    align-items-center
                    p-2
                    border
                    rounded
                    mb-1
                  "
                >
                  <div>
                    <span class="fw-bold">{{ task?.name }}</span>
                    <span
                      v-if="task?.workTaskGroup?.name"
                      class="text-muted ms-1"
                      >({{ task.workTaskGroup.name }})</span
                    >
                    <span
                      class="badge ms-1"
                      :class="getWorkTaskStatusClass(task?.status)"
                      >{{ getWorkTaskStatusName(task?.status) }}</span
                    >
                  </div>
                  <small class="text-muted"
                    >{{ task?.finished_percentage || 0 }}%</small
                  >
                </div>
              </div>
              <div v-else class="small text-muted">No tasks linked yet.</div>
            </div>
          </template>
        </div>

        <!-- Internal Notes Display & Edit -->
        <div class="p-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="text-uppercase small fw-bold text-muted mb-0">
              Internal Notes
            </h6>
            <button
              class="btn btn-link btn-sm text-decoration-none p-0"
              @click="showInternalNote = !showInternalNote"
            >
              <i
                class="bi"
                :class="showInternalNote ? 'bi-x-circle' : 'bi-pencil-square'"
              ></i>
              {{ showInternalNote ? "Cancel" : "Edit" }}
            </button>
          </div>

          <div v-if="!showInternalNote">
            <div
              v-if="selectedThread.internal_notes"
              class="
                p-3
                bg-warning bg-opacity-10
                border border-warning
                rounded
                small
                shadow-sm
              "
            >
              {{ selectedThread.internal_notes }}
            </div>
            <div v-else class="text-muted small italic">
              No internal notes yet.
            </div>
          </div>

          <div v-else class="mt-2">
            <textarea
              class="form-control form-control-sm border-warning"
              rows="4"
              placeholder="Private note (invisible to customer)..."
              v-model="internalNoteText"
            ></textarea>
            <div class="d-grid mt-2">
              <button class="btn btn-warning btn-sm" @click="saveInternalNote">
                Save Note
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Placeholder -->
      <div
        class="col-9 bg-white d-flex align-items-center justify-content-center"
        v-else-if="!selectedThread"
      >
        <div class="text-center opacity-50">
          <BaseIcon name="mail" style="font-size: 4rem" />
          <h4 class="mt-3">Select a conversation to start messaging</h4>
        </div>
      </div>
    </div>

    <!-- Ticket Modal -->
    <div class="modal fade show d-block" v-if="showTicketModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create Support Ticket</h5>
            <button
              type="button"
              class="btn-close"
              @click="showTicketModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Title</label>
              <input
                type="text"
                class="form-control"
                v-model="ticketForm.title"
                placeholder="Brief summary of the issue"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="ticketForm.description"
                placeholder="Detailed description..."
              ></textarea>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Category</label>
                <v-select
                  v-model="ticketForm.contact_category_id"
                  :options="contactCategories"
                  :reduce="(cat) => cat.id"
                  label="display_name"
                  :clearable="false"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Priority</label>
                <v-select
                  v-model="ticketForm.priority"
                  :options="TICKET_PRIORITIES"
                  :reduce="(prio) => prio.id"
                  label="name"
                  :clearable="false"
                />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Assign to User</label>
              <v-select
                v-model="ticketForm.assigned_to_user_id"
                :options="users"
                :reduce="(user) => user.id"
                label="full_name"
                placeholder="Unassigned"
              >
                <template #option="option">
                  {{ option.full_name }} ({{ option.email }})
                </template>
              </v-select>
            </div>

            <div class="mb-3">
              <label class="form-label">Related Tasks</label>
              <div v-if="ticketForm.tasks.length" class="mb-2">
                <div
                  v-for="(task, index) in ticketForm.tasks"
                  :key="index"
                  class="border rounded p-2 mb-2"
                >
                  <div class="row g-2">
                    <div class="col-12">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="task.name"
                        placeholder="Task name"
                      />
                    </div>
                    <div class="col-md-4">
                      <v-select
                        v-model="task.work_bucket_id"
                        :options="workBuckets"
                        :reduce="(b) => b.id"
                        label="name"
                        placeholder="Select Bucket"
                        @update:modelValue="task.work_task_group_id = null"
                      />
                    </div>
                    <div class="col-md-4">
                      <v-select
                        v-model="task.work_task_group_id"
                        :options="getGroupsForBucket(task.work_bucket_id)"
                        :reduce="(g) => g.id"
                        label="name"
                        placeholder="Select Group"
                      />
                    </div>
                    <div class="col-md-3">
                      <v-select
                        v-model="task.user_id"
                        :options="users"
                        :reduce="(u) => u.id"
                        label="full_name"
                        placeholder="Assign to User"
                      >
                        <template #option="option">
                          {{ option.full_name }}
                        </template>
                      </v-select>
                    </div>
                    <div class="col-md-1">
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click="ticketForm.tasks.splice(index, 1)"
                      >
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="addTaskToTicket"
              >
                <i class="bi bi-plus"></i> Add Task
              </button>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showTicketModal = false"
            >
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="createTicket">
              Create Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showTicketModal"></div>

    <DeliveryStatusModal
      ref="deliveryStatusModal"
      :trackingCode="trackingCode"
      :method="trackingMethodId"
      :appId="trackingAppId"
    />
  </div>
</template>

<script>
import { useStore } from "vuex";
import ws from "@/modules/ws";
import http from "@/modules/http";
import date from "@/modules/date";
import deliveredStatuses from "../../../modules/DeliveredStatuses";
import paymentStatuses from "../../../modules/PaymentStatuses";
import orderStatuses from "../../../modules/OrderStatuses";
import BaseIcon from "../../../components/icons/BaseIcon.vue";
import DeliveryStatusModal from "../../../components/modals/DeliveryStatusModal.vue";

import {
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  TICKET_CATEGORIES,
} from "../../../constants/tickets";

export default {
  name: "Messages",
  components: { BaseIcon, DeliveryStatusModal },
  data() {
    return {
      deliveredStatuses: deliveredStatuses,
      paymentStatuses: paymentStatuses,
      statuses: orderStatuses,
      store: useStore(),
      page: 1,
      threads: [],
      hasNextPage: false,
      loading: false,
      reviewingAi: false,
      selectedThread: null,
      newMessageText: "",
      showInternalNote: false,
      internalNoteText: "",
      showTicketModal: false,
      ticketForm: {
        title: "",
        description: "",
        category: "general",
        contact_category_id: null,
        priority: "medium",
        assigned_to_user_id: null,
        tasks: [],
      },
      contactCategories: [],
      users: [],
      workTaskGroups: [],
      workBuckets: [],
      initialThreadId: null,
      trackingCode: "",
      trackingMethodId: null,
      trackingAppId: null,
      editingTicketId: null,
      editingTicketField: null,
      TICKET_STATUSES,
      TICKET_PRIORITIES,
      TICKET_CATEGORIES,
    };
  },
  computed: {
    filteredThreads() {
      if (!this.store.state.topBar.q) return this.threads;
      const q = this.store.state.topBar.q.toLowerCase();
      return this.threads.filter(
        (t) =>
          (t.subject && t.subject.toLowerCase().includes(q)) ||
          (t.email && t.email.toLowerCase().includes(q))
      );
    },
  },
  watch: {
    "$route.query.thread": {
      handler(val) {
        if (val) {
          const threadId = parseInt(val);
          const found = this.threads.find((t) => t.id === threadId);
          if (found) {
            this.selectThread(found);
          } else {
            this.loadSpecificThread(threadId);
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadData();
    this.loadUsers();
    this.loadWorkTaskGroups();
    this.loadContactCategories();
    ws.init();
    ws.subscribe("add", "contactMessage", (e) => {
      if (e.session !== this.sessionId) {
        const msg = e.object;
        const thread = this.threads.find((t) => t.id === msg.message_thread_id);
        if (thread) {
          if (!thread.messages) thread.messages = [];
          thread.messages.push(msg);
          if (this.selectedThread?.id !== thread.id) {
            if (!thread.unread_messages_count) thread.unread_messages_count = 0;
            thread.unread_messages_count++;
          } else {
            // If already open, mark as read on server too
            http.fetch(`/messages/threads/${thread.id}/read`, {}, false, "PUT");
            this.$nextTick(() => this.scrollToBottom());
          }
          // Move thread to top
          const index = this.threads.indexOf(thread);
          this.threads.splice(index, 1);
          this.threads.unshift(thread);
        } else {
          // If thread not found, maybe it's a new thread or we need to reload
          this.page = 1;
          this.loadData();
        }
      }
    });

    if (this.$route.query.thread) {
      this.initialThreadId = parseInt(this.$route.query.thread);
    }
  },
  methods: {
    formatDate(val) {
      return date.format(val, true, false);
    },
    loadUsers() {
      http.fetch("/users").then((data) => {
        this.users = data;
      });
    },
    loadWorkTaskGroups() {
      http.fetch("/work/task-groups").then((data) => {
        this.workTaskGroups = data;
        const bucketsMap = {};
        data.forEach((g) => {
          if (g.work_bucket_id && g.workBucket) {
            if (!bucketsMap[g.work_bucket_id]) {
              bucketsMap[g.work_bucket_id] = g.workBucket;
            }
          }
        });
        this.workBuckets = Object.values(bucketsMap);
      });
    },
    getGroupsForBucket(bucketId) {
      if (!bucketId) return [];
      return this.workTaskGroups.filter((g) => g.work_bucket_id === bucketId);
    },
    loadMore() {
      if (this.loading || !this.hasNextPage) return;
      this.page++;
      this.loadData();
    },
    loadData() {
      this.loading = true;
      let url = `/messages?page=${this.page}`;
      http.fetch(url).then((data) => {
        const newThreads = data.data || [];
        if (this.page === 1) {
          this.threads = newThreads;
        } else {
          // Avoid duplicates
          const existingIds = new Set(this.threads.map((t) => t.id));
          const filteredNew = newThreads.filter((t) => !existingIds.has(t.id));
          this.threads = [...this.threads, ...filteredNew];
        }

        this.hasNextPage = data.next_page_url !== null;
        this.loading = false;
      });
    },
    loadSpecificThread(id) {
      http.fetch(`/messages/threads/${id}`).then((data) => {
        const thread = data.thread || data;
        if (thread) {
          const existingIndex = this.threads.findIndex(
            (t) => t.id === thread.id
          );
          if (existingIndex !== -1) {
            this.threads[existingIndex] = thread;
          } else {
            this.threads.unshift(thread);
          }
          this.selectThread(thread);
        }
      });
    },
    loadContactCategories() {
      http.fetch("/tickets").then((data) => {
        if (data.contactCategories) {
          this.contactCategories = data.contactCategories.map((c) => ({
            ...c,
            display_name: c.translation?.name || c.name,
          }));
        }
      });
    },
    reviewThreadByAi() {
      this.reviewingAi = true;
      http
        .fetch(
          `/messages/threads/${this.selectedThread.id}/review`,
          {},
          false,
          "POST"
        )
        .then((res) => {
          if (res.ticket_needed) {
            this.ticketForm.title = res.title;
            this.ticketForm.description = res.description;
            this.ticketForm.contact_category_id = res.contact_category_id;
            this.ticketForm.priority = res.priority || "medium";
            this.showTicketModal = true;
          }
          this.reviewingAi = false;
        })
        .catch(() => {
          this.reviewingAi = false;
        });
    },
    selectThread(thread) {
      this.selectedThread = thread;
      this.internalNoteText = thread.internal_notes || "";
      this.showInternalNote = false;
      // Mark as read locally and sync with server
      if (thread.unread_messages_count > 0) {
        thread.unread_messages_count = 0;
        http.fetch(`/messages/threads/${thread.id}/read`, {}, false, "PUT");
      }
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    lastMessage(thread) {
      if (!thread.messages || !thread.messages.length) return { text: "" };
      return thread.messages[thread.messages.length - 1];
    },
    getIconForThread(thread) {
      // Simple heuristic for demo
      if (thread.external_user_id) return "social";
      return "mail";
    },
    getTicketPriorityClass(priority) {
      return (
        TICKET_PRIORITIES.find((p) => p.id === priority)?.class ||
        "bg-light text-dark"
      );
    },
    getTicketStatusClass(status) {
      return (
        TICKET_STATUSES.find((s) => s.id === status)?.class ||
        "bg-light text-dark"
      );
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    sendMessage() {
      if (!this.newMessageText.trim()) return;
      // In a real app, this would call an API and update UI via WS or locally
      const msg = {
        id: Date.now(),
        text: this.newMessageText,
        crm_item_id: this.store.state.user.id,
        posted_at: new Date().toISOString(),
      };
      this.selectedThread.messages.push(msg);
      this.newMessageText = "";
      this.$nextTick(() => this.scrollToBottom());
    },
    saveInternalNote() {
      http
        .fetch(
          `/messages/threads/${this.selectedThread.id}`,
          {
            internal_notes: this.internalNoteText,
          },
          false,
          "PUT"
        )
        .then(() => {
          this.selectedThread.internal_notes = this.internalNoteText;
          this.showInternalNote = false;
        });
    },
    createTicket() {
      const payload = {
        ...this.ticketForm,
        create_tasks: this.ticketForm.tasks.filter(
          (t) => t.name && t.work_task_group_id
        ),
      };
      http
        .fetch(
          `/messages/threads/${this.selectedThread.id}/ticket`,
          payload,
          false,
          "POST"
        )
        .then((res) => {
          if (!this.selectedThread.tickets) this.selectedThread.tickets = [];
          this.selectedThread.tickets.push(res.data);
          this.showTicketModal = false;
          this.ticketForm = {
            title: "",
            description: "",
            category: "general",
            contact_category_id: null,
            priority: "medium",
            assigned_to_user_id: null,
            tasks: [],
          };
        });
    },
    addTaskToTicket() {
      this.ticketForm.tasks.push({
        name: "",
        work_bucket_id: null,
        work_task_group_id: null,
        user_id: null,
      });
    },
    updateTicketStatus(ticket) {
      http.fetch(`/tickets/${ticket?.id}`, ticket, false, "PUT");
      this.editingTicketId = null;
      this.editingTicketField = null;
    },
    updateTicketUser(ticket) {
      this.updateTicketStatus(ticket);
    },
    setEditing(ticketId, field) {
      this.editingTicketId = ticketId;
      this.editingTicketField = field;
    },
    getTicketCategoryName(id) {
      return TICKET_CATEGORIES.find((c) => c.id === id)?.name || id;
    },
    getContactCategoryName(id) {
      if (!id) return "General";
      return (
        this.contactCategories.find((c) => c.id == id)?.display_name ||
        "General"
      );
    },
    getTicketStatusName(id) {
      return TICKET_STATUSES.find((s) => s.id === id)?.name || id;
    },
    getTicketPriorityName(id) {
      return TICKET_PRIORITIES.find((p) => p.id === id)?.name || id;
    },
    getUserName(id) {
      if (!id) return "Unassigned";
      return this.users.find((u) => u.id == id)?.full_name || "Unknown User";
    },
    getWorkTaskStatusClass(status) {
      const map = {
        0: "badge-outline",
        1: "bg-info",
        2: "bg-warning",
        3: "bg-success",
        4: "bg-danger",
      };
      return map[status] || "bg-light";
    },
    getWorkTaskStatusName(status) {
      const names = {
        0: "To Do",
        1: "In Progress",
        2: "On Hold",
        3: "Completed",
        4: "Cancelled",
      };
      return names[status] || "Unknown";
    },
    trackShipping(delivery) {
      const order = this.selectedThread.order;
      if (!order) return;

      if (!delivery) {
        delivery =
          order.order_deliveries && order.order_deliveries.length > 0
            ? order.order_deliveries[0]
            : null;
      }

      if (delivery && delivery.public_tracking_code) {
        this.trackingCode = delivery.public_tracking_code;
        this.trackingMethodId = delivery.delivery_method_id;
        this.trackingAppId = order.app_id;
        this.$nextTick(() => {
          this.$refs.deliveryStatusModal.showModal();
        });
      } else {
        alert("No tracking information available for this delivery.");
      }
    },
  },
};
</script>

<style scoped>
.messages-page {
  height: calc(100vh - 100px);
  background: #f8f9fa;
}

.active-thread {
  background-color: #f0f7ff !important;
  border-left: 4px solid #0d6efd !important;
}

.scrollable-list {
  height: calc(100vh - 17em);
  overflow-y: auto;
}

.list-group-item {
  transition: all 0.2s;
  cursor: pointer;
  border-left: 4px solid transparent;
  border-bottom: 1px solid #f1f1f1 !important;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.message-bubble {
  max-width: 75%;
}

.message-bubble.received .message-content {
  background-color: #fff;
  border-bottom-left-radius: 2px !important;
  border: 1px solid #e9ecef;
}

.message-bubble.sent .message-content {
  background-color: #0d6efd;
  color: white;
  border-bottom-right-radius: 2px !important;
  box-shadow: 0 4px 6px rgba(13, 110, 253, 0.15) !important;
}

.message-bubble.sent .text-muted {
  color: rgba(255, 255, 255, 0.7) !important;
}

.small-icon {
  width: 16px;
  height: 16px;
  opacity: 0.6;
}

.z-index-1 {
  z-index: 1;
}
.x-small {
  font-size: 0.75rem;
}
.clickable-field {
  cursor: pointer;
  min-height: 28px;
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.clickable-field:hover {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
.border-transparent {
  border-color: transparent;
}
</style>
